import Swal from "sweetalert2";
import { buildDynamicModelItemViewPath } from "@/utils/dynamicModelPaths";
import { formatNumberSync } from "@/utils/numberFormatter";

/**
 * Popup con las notas comerciales sin aplicar de un contacto.
 *
 * Se abre desde el aviso que va al lado del cliente y del proveedor. Cada fila enlaza a
 * su nota en una pestaña nueva: el aviso aparece sobre formularios a medio cargar y
 * navegar en la misma pestaña perdería lo que el usuario venía escribiendo.
 */

function money(value) {
  try {
    return formatNumberSync(value, null, true);
  } catch (e) {
    return String(value ?? "");
  }
}

function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatDate(value) {
  if (!value) return "";
  // La fecha de la nota es un día suelto ('2026-10-22'): leída como medianoche UTC,
  // en América cae en el día anterior. Se arma en hora local.
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

/** Crédito o débito, para distinguir de un vistazo notas que mueven el saldo al revés. */
function kindLabel(note, translate) {
  const cd = String(note.credit_debit || "").toLowerCase();
  if (cd.startsWith("debit")) return translate("notaComercial.pending.kindDebit");
  if (cd.startsWith("credit")) return translate("notaComercial.pending.kindCredit");
  return "";
}

function noteRowHtml(note, translate) {
  const label = note.number || note.name || note._id;
  const href = buildDynamicModelItemViewPath("notacomercial", note._id, note.name);
  const kind = kindLabel(note, translate);
  const date = formatDate(note.date);
  const meta = [kind, date].filter(Boolean).join(" · ");
  return `
    <a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer"
       style="display:flex;align-items:center;justify-content:space-between;gap:12px;
              text-align:left;text-decoration:none;color:inherit;
              border:1px solid #dee2e6;border-radius:6px;padding:8px 10px;margin:6px 0;">
      <span style="min-width:0;">
        <strong style="word-break:break-word;">${escapeHtml(label)}</strong>
        ${meta ? `<br/><span style="font-size:12px;color:#6c757d;">${escapeHtml(meta)}</span>` : ""}
      </span>
      <span style="text-align:right;white-space:nowrap;">
        <strong>${escapeHtml(money(note.remaining))}</strong>
        <br/><span style="font-size:11px;color:#6c757d;">
          ${escapeHtml(translate("notaComercial.pending.ofTotal"))} ${escapeHtml(money(note.amount))}
        </span>
      </span>
    </a>`;
}

/**
 * @param {{ count: number, total: number, notes: object[] }} summary
 * @param {{ contactName?: string, t?: (key: string, params?: object) => string }} options
 */
export function showNotaComercialPendingPopup(summary, options = {}) {
  const translate =
    typeof options.t === "function" ? options.t : (key) => key;
  const notes = Array.isArray(summary?.notes) ? summary.notes : [];
  if (!notes.length) return Promise.resolve();

  const contactName = String(options.contactName || "").trim();
  const notShown = Math.max(0, Number(summary.count || 0) - notes.length);
  const listHref = "/dynamic-model/notacomercial";

  const headerHtml = `
    <p style="text-align:left;color:#6c757d;margin:0 0 8px;">
      ${escapeHtml(
        translate("notaComercial.pending.popupSubtitle", {
          count: Number(summary.count || 0),
          amount: money(summary.total),
        })
      )}
    </p>`;

  const footerHtml = notShown
    ? `<p style="text-align:left;font-size:12px;color:#6c757d;margin:8px 0 0;">
         ${escapeHtml(translate("notaComercial.pending.popupMore", { count: notShown }))}
       </p>`
    : "";

  return Swal.fire({
    title: contactName || translate("notaComercial.pending.popupTitle"),
    html: `${headerHtml}${notes.map((n) => noteRowHtml(n, translate)).join("")}${footerHtml}`,
    showCancelButton: true,
    confirmButtonText: translate("notaComercial.pending.popupOpenList"),
    cancelButtonText: translate("notaComercial.pending.popupClose"),
    focusConfirm: false,
    width: 620,
    heightAuto: false,
    customClass: { container: "nc-pending-swal-container" },
    didOpen: () => {
      const el = document.querySelector(".nc-pending-swal-container");
      if (el) el.style.zIndex = "20000";
    },
  }).then((result) => {
    if (result.isConfirmed) window.open(listHref, "_blank", "noopener");
  });
}
